<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&h=800&fit=crop" alt="" class="w-full h-full object-cover">
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <nav class="text-sm text-white/70 mb-6">
      <a routerLink="/" class="hover:text-white">Home</a>
      <span class="mx-2">/</span>
      <a routerLink="/cloud-compliance" class="hover:text-white">Cloud Audits & Compliance</a>
      <span class="mx-2">/</span>
      <span class="text-white">HIPAA Compliance</span>
    </nav>
    <div class="text-center text-white">
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">HIPAA Compliance</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto mb-8">
        Protect patient health information and achieve comprehensive HIPAA compliance for healthcare applications.
      </p>
      <a routerLink="/contact" class="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
        Get Your Compliance Assessment
      </a>
    </div>
  </div>
</section>

<!-- Features Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our HIPAA Services</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Comprehensive solutions to achieve and maintain HIPAA compliance.
      </p>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (feature of features; track feature.title) {
        <div class="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
          <div class="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6">
            <span class="text-2xl">{{ feature.icon }}</span>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ feature.title }}</h3>
          <p class="text-gray-600">{{ feature.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Process Section -->
<section class="py-20 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Compliance Process</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        A proven methodology to achieve HIPAA compliance efficiently.
      </p>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (step of process; track step.number) {
        <div class="text-center">
          <div class="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <span class="text-2xl font-bold text-white">{{ step.number }}</span>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ step.title }}</h3>
          <p class="text-gray-600">{{ step.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Case Study Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Case Study</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        See how we helped achieve HIPAA compliance.
      </p>
    </div>
    <div class="bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl overflow-hidden">
      <div class="p-8 lg:p-12">
        <div class="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
          {{ caseStudy.industry }}
        </div>
        <h3 class="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{{ caseStudy.title }}</h3>
        <p class="text-gray-600 mb-6">{{ caseStudy.description }}</p>

        <div class="space-y-6">
          <div>
            <h4 class="font-semibold text-gray-900 mb-2">The Challenge</h4>
            <p class="text-gray-600">{{ caseStudy.challenge }}</p>
          </div>
          <div>
            <h4 class="font-semibold text-gray-900 mb-2">Our Solution</h4>
            <p class="text-gray-600">{{ caseStudy.solution }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
          <div class="bg-white rounded-xl p-4 shadow-sm text-center">
            <div class="text-2xl font-bold text-primary-600">{{ caseStudy.industry }}</div>
            <div class="text-sm text-gray-600">Industry</div>
          </div>
          <div class="bg-white rounded-xl p-4 shadow-sm text-center">
            <div class="text-2xl font-bold text-primary-600">{{ caseStudy.timeline }}</div>
            <div class="text-sm text-gray-600">Timeline</div>
          </div>
          <div class="bg-white rounded-xl p-4 shadow-sm text-center">
            <div class="text-2xl font-bold text-primary-600">{{ caseStudy.result }}</div>
            <div class="text-sm text-gray-600">Result</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Testimonial Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <svg class="w-12 h-12 text-white/50 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
    </svg>
    <p class="text-xl md:text-2xl text-white mb-8 italic leading-relaxed">
      "{{ testimonial.quote }}"
    </p>
    <div class="text-white">
      <div class="font-semibold text-lg">{{ testimonial.author }}</div>
      <div class="text-white/70">{{ testimonial.role }}</div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-white">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Secure Your Healthcare Platform</h2>
    <p class="text-xl text-gray-600 mb-8">
      Achieve HIPAA compliance and protect patient trust with expert guidance.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a routerLink="/contact" class="btn-primary">Schedule Consultation</a>
      <a routerLink="/cloud-compliance" class="btn-secondary">Explore All Compliance Services</a>
    </div>
  </div>
</section>
